import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useConges, useStructures } from '@/api/hooks';
import { CATEGORIES_CONGE, LIBELLE_CATEGORIE_CONGE, LIBELLE_ETAPE, LIBELLE_STATUT_CONGE, type StatutConge } from '@/api/types';
import { accedeModule } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { LienConge, StatutCongeBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dateFr } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT_CONGE) as StatutConge[];

/** Demandes de congé : à traiter (validateurs), périmètre (directeurs, RH, DGA, DG) et les miennes. */
export function CongesListe() {
  const u = useSession()!.utilisateur;
  const perimetre = accedeModule(u.profils, 'calendrier');
  const onglets = perimetre ? (['a-traiter', 'perimetre', 'mes'] as const) : (['a-traiter', 'mes'] as const);
  type Onglet = (typeof onglets)[number];
  const [params, setParams] = useSearchParams();
  const o = params.get('onglet');
  const onglet: Onglet = o && (onglets as readonly string[]).includes(o) ? (o as Onglet) : 'a-traiter';
  const setOnglet = (v: Onglet) => setParams(v === 'a-traiter' ? {} : { onglet: v }, { replace: true });
  const [statut, setStatut] = useState<StatutConge | 'TOUS'>('TOUS');
  const [structure, setStructure] = useState('');
  const [categorie, setCategorie] = useState('');
  const structures = useStructures();
  const { data, isLoading, error } = useConges({ onglet, statut: onglet !== 'a-traiter' && statut !== 'TOUS' ? statut : undefined, structure: onglet === 'perimetre' && structure ? structure : undefined, categorie: categorie || undefined });
  return (
    <div>
      <PageHeader
        titre="Congés"
        sousTitre="Demandes à traiter, demandes de votre périmètre et vos propres demandes."
        actions={
          <Button asChild>
            <Link to="/rh/conges/nouvelle">
              <Plus className="size-4" /> Nouvelle demande de congé
            </Link>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as Onglet)}>
          <TabsList>
            <TabsTrigger value="a-traiter">À traiter</TabsTrigger>
            {perimetre && <TabsTrigger value="perimetre">Mon périmètre</TabsTrigger>}
            <TabsTrigger value="mes">Mes demandes</TabsTrigger>
          </TabsList>
        </Tabs>
        {onglet !== 'a-traiter' && (
          <SelectNatif value={statut} onChange={(e) => setStatut(e.target.value as StatutConge | 'TOUS')}>
            <option value="TOUS">Tous les statuts</option>
            {STATUTS.map((s) => (
              <option key={s} value={s}>
                {LIBELLE_STATUT_CONGE[s]}
              </option>
            ))}
          </SelectNatif>
        )}
        {onglet === 'perimetre' && (
          <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)}>
            <option value="">Toutes les structures</option>
            {(structures.data ?? []).map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} · {s.libelle}
              </option>
            ))}
          </SelectNatif>
        )}
        <SelectNatif value={categorie} onChange={(e) => setCategorie(e.target.value)}>
          <option value="">Toutes les catégories</option>
          {CATEGORIES_CONGE.map((c) => (
            <option key={c} value={c}>
              {LIBELLE_CATEGORIE_CONGE[c]}
            </option>
          ))}
        </SelectNatif>
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucune demande de congé en attente de votre décision.' : 'Aucune demande de congé.'} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Catégorie</TableHead>
                <TableHead>Absence</TableHead>
                <TableHead className="text-right">Jours</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <LienConge id={c.id} numero={c.numero} />
                    {c.avantDouzeMois && <span className="ml-1 text-xs text-amber-700">avant 12 mois</span>}
                  </TableCell>
                  <TableCell>
                    {c.agent.nom}
                    <div className="text-xs text-muted-foreground">{c.structure.code}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{c.libelleCategorie}</TableCell>
                  <TableCell>
                    du {dateFr(c.dateDebut)} au {dateFr(c.dernierJour)}
                  </TableCell>
                  <TableCell className="num text-right">{c.joursPris ?? c.joursOuvrables}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutCongeBadge statut={c.statut} />
                    {c.statut === 'SOUMIS' && c.etapeCourante && <Badge variant="secondary">{LIBELLE_ETAPE[c.etapeCourante]}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
